import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MdCloudUpload, MdRefresh, MdUploadFile } from "react-icons/md";
import MasterResultsPreview from "components/results/MasterResultsPreview";
import Card from "components/card";
import { adminFetch } from "utils/adminFetch";

function formatPnl(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const n = Number(value);
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(2)} USD`;
}

const LIVE_UPDATE_MS = 60_000;

function buildQuery({ masterId, refresh, live, excludedSymbols, periodFrom, periodTo, preset }) {
  const params = new URLSearchParams();
  params.set("masterId", masterId);
  if (refresh) params.set("refresh", "1");
  if (live) params.set("live", "1");
  if (excludedSymbols?.length) {
    params.set("excluded", excludedSymbols.join(","));
  }
  if (periodFrom) params.set("from", periodFrom);
  if (periodTo) params.set("to", periodTo);
  if (preset) params.set("preset", preset);
  return `?${params.toString()}`;
}

export default function AdminMasterAccountPanel({ masterSummary, onRefreshAll }) {
  const masterId = masterSummary.id;
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [payload, setPayload] = useState(null);
  const [includedKeys, setIncludedKeys] = useState(new Set());
  const [dirty, setDirty] = useState(false);
  const [periodPreset, setPeriodPreset] = useState("all");
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [importing, setImporting] = useState(false);
  const [liveUpdate, setLiveUpdate] = useState(Boolean(masterSummary.liveUpdateEnabled));
  const [liveUpdateBusy, setLiveUpdateBusy] = useState(false);
  const fileInputRef = useRef(null);

  const loadData = useCallback(
    async (refresh = false, overrides = {}) => {
      const isApply = overrides.apply === true;
      if (isApply) setApplying(true);
      else setLoading(true);
      setError("");
      try {
        const from = overrides.periodFrom ?? periodFrom;
        const to = overrides.periodTo ?? periodTo;
        const preset = overrides.periodPreset ?? periodPreset;
        const excluded =
          overrides.excludedSymbols ??
          (payload?.pairs || [])
            .map((p) => p.symbolKey)
            .filter((key) => key && !includedKeys.has(key));

        const qs = buildQuery({
          masterId,
          refresh,
          excludedSymbols:
            overrides.excludedSymbols != null || dirty ? excluded : undefined,
          periodFrom: from,
          periodTo: to,
          preset,
        });

        const res = await adminFetch(`/api/admin/master-results${qs}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.ok) {
          setError(data.message || "Could not load master results.");
          if (!payload) setPayload(null);
          return;
        }

        setPayload(data);
        const included = new Set(
          (data.pairs || [])
            .filter((p) => p.included !== false)
            .map((p) => p.symbolKey),
        );
        setIncludedKeys(included);
        setPeriodPreset(data.periodPreset || preset || "custom");
        if (overrides.keepPeriod || isApply) {
          setPeriodFrom(from);
          setPeriodTo(to);
        } else {
          setPeriodFrom(data.periodFrom || "");
          setPeriodTo(data.periodTo || "");
        }
        setDirty(false);
        setLiveUpdate(Boolean(data.liveUpdateEnabled));
        if (isApply) {
          setMessage(`Filtre appliqué — ${data.tradeCount ?? 0} trade(s) sur la période.`);
        }
      } catch {
        setError("Network error while loading master results.");
        if (!payload) setPayload(null);
      } finally {
        if (isApply) setApplying(false);
        else setLoading(false);
      }
    },
    [masterId, dirty, includedKeys, payload, periodFrom, periodTo, periodPreset],
  );

  const runLiveUpdate = useCallback(async () => {
    try {
      const qs = buildQuery({
        masterId,
        live: true,
      });

      const res = await adminFetch(`/api/admin/master-results${qs}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) return;

      setPayload(data);
      setLiveUpdate(Boolean(data.liveUpdateEnabled));

      await adminFetch("/api/admin/master-results/publish", {
        method: "POST",
        body: JSON.stringify({
          masterId,
          liveAutoPublish: true,
          refresh: false,
        }),
      });
    } catch {
      // silent background refresh
    }
  }, [masterId]);

  useEffect(() => {
    if (!liveUpdate) return undefined;
    void runLiveUpdate();
    const timer = window.setInterval(() => void runLiveUpdate(), LIVE_UPDATE_MS);
    return () => window.clearInterval(timer);
  }, [liveUpdate, runLiveUpdate]);

  const toggleLiveUpdate = async () => {
    const next = !liveUpdate;
    const prev = liveUpdate;
    setLiveUpdate(next);
    setLiveUpdateBusy(true);
    setMessage("");
    setError("");
    try {
      const res = await adminFetch("/api/admin/master-results/live-update", {
        method: "POST",
        body: JSON.stringify({ masterId, enabled: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setLiveUpdate(prev);
        setError(data.message || "Impossible de modifier LiveUpdate.");
        return;
      }
      const enabled = Boolean(data.liveUpdateEnabled);
      setLiveUpdate(enabled);
      if (data.publishWarning) {
        setError(String(data.publishWarning));
      }
      setMessage(
        enabled
          ? `LiveUpdate ON — publication auto sur /Results${data.tradeCount != null ? ` (${data.tradeCount} trades)` : ""}.`
          : "LiveUpdate OFF — choisis From, To et les paires.",
      );
      onRefreshAll?.();
      if (enabled) {
        setPeriodPreset("all");
        setPeriodFrom("");
        setPeriodTo("");
        setDirty(false);
        void runLiveUpdate();
      }
    } catch {
      setLiveUpdate(prev);
      setError("Erreur réseau — LiveUpdate non modifié.");
    } finally {
      setLiveUpdateBusy(false);
    }
  };

  useEffect(() => {
    const enabled = Boolean(masterSummary.liveUpdateEnabled);
    setLiveUpdate(enabled);
  }, [masterId, masterSummary.liveUpdateEnabled]);

  useEffect(() => {
    void loadData(false);
  }, [masterId]);

  useEffect(() => {
    if (liveUpdate || periodFrom || periodTo) return;
    if (payload?.historyFirstDate && payload?.historyLastDate) {
      setPeriodFrom(payload.historyFirstDate);
      setPeriodTo(payload.historyLastDate);
      setPeriodPreset("custom");
    }
  }, [
    payload?.historyFirstDate,
    payload?.historyLastDate,
    liveUpdate,
    periodFrom,
    periodTo,
  ]);

  const excludedSymbols = useMemo(() => {
    if (!payload?.pairs?.length) return [];
    return payload.pairs
      .map((p) => p.symbolKey)
      .filter((key) => key && !includedKeys.has(key));
  }, [payload, includedKeys]);

  const canShare =
    !liveUpdate &&
    Boolean(periodFrom) &&
    Boolean(periodTo) &&
    includedKeys.size > 0 &&
    !loading &&
    !applying &&
    !publishing;

  const shareBlockReason = useMemo(() => {
    if (liveUpdate) return "Désactive LiveUpdate pour partager.";
    if (!periodFrom || !periodTo) return "Renseigne From et To, puis clique Appliquer.";
    if (includedKeys.size === 0) return "Coche au moins une paire.";
    if (loading || applying) return "Chargement en cours…";
    if (publishing) return "Partage en cours…";
    return "";
  }, [
    liveUpdate,
    periodFrom,
    periodTo,
    includedKeys.size,
    loading,
    applying,
    publishing,
  ]);

  const applyFilters = useCallback(async () => {
    if (!periodFrom || !periodTo) {
      setError("Renseigne From et To avant d'appliquer.");
      return;
    }
    if (periodFrom > periodTo) {
      setError("La date From doit être avant To.");
      return;
    }
    setMessage("");
    setError("");
    await loadData(false, {
      apply: true,
      keepPeriod: true,
      periodFrom,
      periodTo,
      periodPreset: "custom",
      excludedSymbols,
    });
  }, [loadData, periodFrom, periodTo, excludedSymbols]);

  useEffect(() => {
    if (liveUpdate || !dirty || !payload) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const qs = buildQuery({
            masterId,
            excludedSymbols,
            periodFrom,
            periodTo,
            preset: periodPreset,
          });
          const res = await adminFetch(`/api/admin/master-results${qs}`);
          const data = await res.json().catch(() => ({}));
          if (!res.ok || !data.ok || cancelled) return;
          setPayload((prev) =>
            prev
              ? {
                  ...prev,
                  periods: data.periods,
                  dailyRows: data.dailyRows,
                  trades: data.trades,
                  tradeCount: data.tradeCount,
                  firstTradingDate: data.firstTradingDate,
                  lastTradingDate: data.lastTradingDate,
                  pairs: data.pairs,
                }
              : data,
          );
        } catch {
          // keep local selection
        }
      })();
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [dirty, excludedSymbols, liveUpdate, masterId, periodFrom, periodTo, periodPreset, payload]);

  const togglePair = (symbolKey) => {
    if (liveUpdate) return;
    setIncludedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(symbolKey)) next.delete(symbolKey);
      else next.add(symbolKey);
      return next;
    });
    setDirty(true);
    setMessage("");
  };

  const selectAllPairs = () => {
    if (liveUpdate) return;
    setIncludedKeys(new Set((payload?.pairs || []).map((p) => p.symbolKey)));
    setDirty(true);
    setMessage("");
  };

  const clearAllPairs = () => {
    if (liveUpdate) return;
    setIncludedKeys(new Set());
    setDirty(true);
    setMessage("");
  };

  const importMt4Html = async (file) => {
    if (!file) return;
    setImporting(true);
    setMessage("");
    setError("");
    try {
      const name = String(file.name || "").toLowerCase();
      if (name && !/\.(html?|htm)$/i.test(name)) {
        setError(
          `Fichier « ${file.name} » : il faut un rapport MT4 en .html (pas .pdf / .csv). Dans MT4 : Historique → clic droit → Rapport.`,
        );
        return;
      }
      const html = await file.text();
      if (!html.trim()) {
        setError("Le fichier est vide.");
        return;
      }
      if (html.length > 20 * 1024 * 1024) {
        setError("Fichier trop gros (> 20 Mo). Réduis la période exportée dans MT4 puis réessaie.");
        return;
      }
      const res = await adminFetch("/api/admin/master-results/import-mt4-html", {
        method: "POST",
        body: JSON.stringify({ masterId, html }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        const detail =
          data.message ||
          (res.status === 413
            ? "Fichier trop volumineux pour le serveur — redémarre npm run dev puis réessaie."
            : res.status === 401
              ? "Session admin expirée — reconnecte-toi sur /admin/login."
              : null);
        setError(detail || `Import MT4 impossible (HTTP ${res.status}).`);
        return;
      }
      setPayload((prev) =>
        prev
          ? {
              ...prev,
              periods: data.periods,
              dailyRows: data.dailyRows,
              firstTradingDate: data.firstTradingDate,
              lastTradingDate: data.lastTradingDate,
              historyFirstDate: data.historyFirstDate,
              historyLastDate: data.historyLastDate,
              pairs: data.pairs,
              trades: data.trades,
              tradeCount: data.tradeCount,
              totalTradeCount: data.totalTradeCount,
            }
          : null,
      );
      setPeriodPreset("all");
      setPeriodFrom("");
      setPeriodTo("");
      setDirty(false);
      setMessage(
        data.message ||
          `${data.addedCount ?? 0} trade(s) ajouté(s), ${data.skippedDuplicates ?? 0} doublon(s) ignoré(s) — total ${data.totalTradeCount ?? 0}.`,
      );
      await loadData(false);
      onRefreshAll?.();
    } catch {
      setError("Erreur réseau pendant l'import.");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const publishToResults = async () => {
    if (liveUpdate) {
      setError("Désactive LiveUpdate pour partager une période et des paires précises.");
      return;
    }
    if (!periodFrom || !periodTo) {
      setError("Choisis une période (date de début et date de fin) avant de partager.");
      return;
    }
    if (includedKeys.size === 0) {
      setError("Sélectionne au moins une paire à afficher sur /Results.");
      return;
    }
    setPublishing(true);
    setMessage("");
    setError("");
    try {
      const res = await adminFetch("/api/admin/master-results/publish", {
        method: "POST",
        body: JSON.stringify({
          masterId,
          excludedSymbols,
          periodFrom,
          periodTo,
          periodPreset: "custom",
          refresh: false,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.message || "Could not publish to /Results.");
        return;
      }
      setPayload((prev) =>
        prev
          ? {
              ...prev,
              excludedSymbols: data.excludedSymbols,
              published: {
                publishedAt: data.publishedAt,
                excludedSymbols: data.excludedSymbols,
                tradeCount: data.tradeCount,
                balanceUsd: data.balanceUsd,
                periodFrom: data.periodFrom,
                periodTo: data.periodTo,
              },
              periods: data.periods,
              dailyRows: data.dailyRows,
              firstTradingDate: data.firstTradingDate,
              lastTradingDate: data.lastTradingDate,
              pairs: data.pairs,
              trades: data.trades,
              tradeCount: data.tradeCount,
            }
          : null,
      );
      setDirty(false);
      setMessage(
        `Partagé sur /Results (${data.tradeCount ?? 0} trades · ${data.periodFrom} → ${data.periodTo} · ${(data.excludedSymbols || []).length} paire(s) exclue(s)).`,
      );
      onRefreshAll?.();
    } catch {
      setError("Network error while publishing.");
    } finally {
      setPublishing(false);
    }
  };

  const publishedAt = payload?.published?.publishedAt;
  const historyLabel =
    payload?.historyFirstDate && payload?.historyLastDate
      ? `${payload.historyFirstDate} → ${payload.historyLastDate}`
      : "";

  return (
    <div className="flex flex-col gap-5">
      <Card extra="w-full p-5">
        <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg font-bold text-navy-700 dark:text-white">
              {payload?.master?.name || masterSummary.name || "Master account"}
            </h2>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              ID Duplikium: {masterId}
              {payload?.master?.accountNumber
                ? ` · Compte ${payload.master.accountNumber}`
                : masterSummary.accountNumber
                  ? ` · Compte ${masterSummary.accountNumber}`
                  : ""}
              {payload?.master?.server || masterSummary.server
                ? ` · ${payload?.master?.server || masterSummary.server}`
                : ""}
            </p>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              {payload?.totalTradeCount ?? masterSummary.totalTradeCount ?? 0} trades
              chargés
              {historyLabel ? ` · Historique ${historyLabel}` : ""}
              {payload?.master?.connected != null
                ? ` · ${payload.master.connected ? "Connecté" : "Déconnecté"}`
                : ""}
              {payload?.backfillComplete === false
                ? " · historique encore en chargement"
                : ""}
            </p>
            <p className="mt-1 text-xs font-semibold text-brand-600 dark:text-brand-300">
              Affichage: {payload?.tradeCount ?? 0} trades
              {periodFrom || periodTo
                ? ` · période ${periodFrom || "…"} → ${periodTo || "…"}`
                : " · toute la période"}
              {payload?.master?.historyStartDate
                ? ` · historique attendu depuis ${payload.master.historyStartDate}`
                : ""}
            </p>
            {payload?.historyFirstDate &&
            payload?.master?.historyStartDate &&
            payload.historyFirstDate > payload.master.historyStartDate ? (
              <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
                Duplikium ne remonte que depuis le{" "}
                <strong>{payload.historyFirstDate}</strong>. Pour l'historique du{" "}
                <strong>{payload.master.historyStartDate}</strong>, exporte le rapport
                MT4 (Historique du compte → clic droit → Rapport) puis clique{" "}
                <strong>Importer rapport MT4</strong> ci-dessous. Les trades déjà
                présents dans Duplikium (ex. juillet) ne seront pas dupliqués.
              </p>
            ) : null}
          </div>
          <div className="mb-4 flex flex-col gap-2 rounded-xl border-2 border-brand-200 bg-brand-50/50 p-4 dark:border-brand-500/30 dark:bg-brand-500/5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-bold text-navy-700 dark:text-white">LiveUpdate</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {liveUpdateBusy
                  ? "Changement en cours…"
                  : liveUpdate
                    ? "ON — mise à jour auto /Results"
                    : "OFF — partage manuel From/To/Pairs"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  if (!liveUpdate && !liveUpdateBusy) void toggleLiveUpdate();
                }}
                disabled={liveUpdateBusy}
                className={`min-w-[5rem] rounded-l-xl px-5 py-3 text-sm font-bold transition ${
                  liveUpdate
                    ? "bg-emerald-600 text-white ring-2 ring-emerald-400"
                    : "border border-gray-300 bg-white text-gray-500 dark:border-white/20 dark:bg-navy-800"
                } disabled:opacity-50`}
              >
                ON
              </button>
              <button
                type="button"
                onClick={() => {
                  if (liveUpdate && !liveUpdateBusy) void toggleLiveUpdate();
                }}
                disabled={liveUpdateBusy}
                className={`min-w-[5rem] rounded-r-xl px-5 py-3 text-sm font-bold transition ${
                  !liveUpdate
                    ? "bg-gray-700 text-white ring-2 ring-gray-400 dark:bg-gray-200 dark:text-navy-900"
                    : "border border-gray-300 bg-white text-gray-500 dark:border-white/20 dark:bg-navy-800"
                } disabled:opacity-50`}
              >
                OFF
              </button>
              {liveUpdateBusy ? (
                <span className="text-xs font-semibold text-brand-600 dark:text-brand-300">
                  …
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {error ? (
          <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">
            {error}
          </p>
        ) : null}
        {message ? (
          <p className="mb-3 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700 dark:bg-green-500/10 dark:text-green-300">
            {message}
          </p>
        ) : null}

        <div className="mb-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-navy-900">
          <div className="flex flex-col gap-3 border-b border-gray-200 pb-4 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {liveUpdate
                ? "From / To / Pairs désactivés"
                : "Remplis From, To, coche Pairs, puis Partager"}
            </p>
            <button
              type="button"
              onClick={() => void loadData(true)}
              disabled={loading}
              className="inline-flex items-center gap-1 rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold text-navy-700 hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
            >
              <MdRefresh className="h-4 w-4" />
              Refresh
            </button>
          </div>

          <div
            className={`mt-4 flex flex-wrap items-end gap-4 ${
              liveUpdate ? "pointer-events-none opacity-40" : ""
            }`}
          >
            <label className="text-xs font-semibold text-navy-700 dark:text-gray-300">
              From:
              <input
                type="date"
                value={periodFrom}
                disabled={liveUpdate}
                min={payload?.historyFirstDate || undefined}
                max={periodTo || payload?.historyLastDate || undefined}
                onChange={(e) => {
                  setPeriodFrom(e.target.value);
                  setPeriodPreset("custom");
                  setDirty(true);
                  setMessage("");
                }}
                className="mt-1 block min-w-[10.5rem] rounded-lg border border-gray-300 px-2 py-1.5 text-sm disabled:cursor-not-allowed dark:border-white/15 dark:bg-navy-800 dark:text-white"
              />
            </label>
            <label className="text-xs font-semibold text-navy-700 dark:text-gray-300">
              To:
              <input
                type="date"
                value={periodTo}
                disabled={liveUpdate}
                min={periodFrom || payload?.historyFirstDate || undefined}
                max={payload?.historyLastDate || undefined}
                onChange={(e) => {
                  setPeriodTo(e.target.value);
                  setPeriodPreset("custom");
                  setDirty(true);
                  setMessage("");
                }}
                className="mt-1 block min-w-[10.5rem] rounded-lg border border-gray-300 px-2 py-1.5 text-sm disabled:cursor-not-allowed dark:border-white/15 dark:bg-navy-800 dark:text-white"
              />
            </label>
            {!liveUpdate ? (
              <button
                type="button"
                onClick={() => void applyFilters()}
                disabled={applying || !periodFrom || !periodTo}
                className="rounded-lg bg-brand-500 px-3 py-2 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
              >
                {applying ? "Application…" : "Appliquer"}
              </button>
            ) : null}
          </div>

          <div
            className={`mt-5 ${liveUpdate ? "pointer-events-none opacity-40" : ""}`}
          >
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-bold text-navy-700 dark:text-white">Pairs:</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={selectAllPairs}
                  disabled={liveUpdate}
                  className="rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-semibold text-navy-700 hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:text-white"
                >
                  Tout cocher
                </button>
                <button
                  type="button"
                  onClick={clearAllPairs}
                  disabled={liveUpdate}
                  className="rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-semibold text-navy-700 hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:text-white"
                >
                  Tout décocher
                </button>
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto rounded-xl border border-gray-200 dark:border-white/10">
              {loading ? (
                <p className="p-4 text-sm text-gray-500">Chargement des paires…</p>
              ) : !payload?.pairs?.length ? (
                <p className="p-4 text-sm text-gray-500">
                  Aucune paire — choisis From/To puis Appliquer.
                </p>
              ) : (
                <ul className="grid grid-cols-1 divide-y divide-gray-100 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 sm:divide-y-0 dark:divide-white/10">
                  {payload.pairs.map((pair) => {
                    const checked = includedKeys.has(pair.symbolKey);
                    return (
                      <li
                        key={pair.symbolKey}
                        className="border-b border-gray-100 last:border-b-0 dark:border-white/10 sm:border-b-0"
                      >
                        <label className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-white/5">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={liveUpdate}
                            onChange={() => togglePair(pair.symbolKey)}
                            className="h-4 w-4 shrink-0 rounded border-gray-300 text-brand-500"
                          />
                          <span className="truncate text-sm font-medium text-navy-700 dark:text-white">
                            {pair.symbol}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({pair.trades})
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <div className="sticky bottom-0 z-10 mt-4 flex flex-col gap-2 border-t border-gray-200 bg-white pt-4 dark:border-white/10 dark:bg-navy-900">
            <div className="flex flex-wrap gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".html,.htm"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void importMt4Html(file);
                }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing || loading}
                className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100"
              >
                <MdUploadFile className="h-4 w-4" />
                {importing ? "Import…" : "Importer rapport MT4"}
              </button>
              <button
                type="button"
                onClick={publishToResults}
                disabled={!canShare}
                title={shareBlockReason || "Publier sur la page /Results"}
                className="inline-flex min-w-[11rem] flex-1 items-center justify-center gap-1 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-bold text-white shadow-md hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 disabled:shadow-none dark:disabled:bg-white/10 dark:disabled:text-gray-500 sm:flex-none"
              >
                <MdCloudUpload className="h-5 w-5" />
                {publishing ? "Partage…" : "Partager sur /Results"}
              </button>
            </div>
            {!canShare && shareBlockReason ? (
              <p className="text-xs text-amber-700 dark:text-amber-200">{shareBlockReason}</p>
            ) : null}
          </div>

          {publishedAt ? (
            <p className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
              Dernier partage /Results: {new Date(publishedAt).toLocaleString()}
              {payload?.published?.tradeCount != null
                ? ` · ${payload.published.tradeCount} trades`
                : ""}
              {payload?.published?.periodFrom && payload?.published?.periodTo
                ? ` · ${payload.published.periodFrom} → ${payload.published.periodTo}`
                : liveUpdate
                  ? " · LiveUpdate (complet)"
                  : ""}
            </p>
          ) : null}
        </div>
      </Card>

      <Card extra="p-0 overflow-hidden">
        <MasterResultsPreview
          periods={payload?.periods}
          dailyRows={payload?.dailyRows}
          balanceUsd={payload?.balanceUsd}
          loading={loading}
          error={error}
          firstTradingDate={payload?.firstTradingDate}
          lastTradingDate={payload?.lastTradingDate}
          sourceNote={
            payload?.source
              ? `${payload.source} · ${payload?.tradeCount ?? 0} trades (paires + période)`
              : ""
          }
        />
      </Card>

      <Card extra="w-full p-5">
        <h3 className="text-lg font-bold text-navy-700 dark:text-white">
          Historique des trades
        </h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Dernières positions fermées (paires + période sélectionnées).
        </p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-b border-gray-200 text-xs uppercase text-gray-500 dark:border-white/10">
              <tr>
                <th className="px-2 py-2">Clôture</th>
                <th className="px-2 py-2">Symbole</th>
                <th className="px-2 py-2">Side</th>
                <th className="px-2 py-2">Pips</th>
                <th className="px-2 py-2">PnL USD</th>
                <th className="px-2 py-2">Ticket</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-2 py-4 text-gray-500">
                    Chargement…
                  </td>
                </tr>
              ) : (payload?.trades || []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-2 py-4 text-gray-500">
                    Aucun trade pour cette sélection.
                  </td>
                </tr>
              ) : (
                (payload?.trades || []).map((t) => (
                  <tr
                    key={`${t.ticket}-${t.closeTime}-${t.symbol}`}
                    className="border-b border-gray-100 dark:border-white/5"
                  >
                    <td className="px-2 py-2 text-gray-600 dark:text-gray-300">
                      {t.closeTime ? new Date(t.closeTime).toLocaleString() : "—"}
                    </td>
                    <td className="px-2 py-2 font-medium text-navy-700 dark:text-white">
                      {t.symbol}
                    </td>
                    <td className="px-2 py-2">{t.side || "—"}</td>
                    <td className="px-2 py-2">
                      {t.pips != null ? Number(t.pips).toFixed(1) : "—"}
                    </td>
                    <td
                      className={`px-2 py-2 font-semibold ${
                        t.pnlUsd > 0
                          ? "text-green-600 dark:text-green-400"
                          : t.pnlUsd < 0
                            ? "text-red-600 dark:text-red-400"
                            : ""
                      }`}
                    >
                      {formatPnl(t.pnlUsd)}
                    </td>
                    <td className="px-2 py-2 text-xs text-gray-500">
                      {t.ticket || "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
